/**
 * Rows of the live ERP shortage report. The ERP's balance leads each row as
 * the source of truth; Helix's figures follow with the difference, and every
 * line Helix has out of line with the ERP is highlighted with what it needs.
 */
import type { ErpShortageAlignment, ErpShortageRow } from '../../lib/api';
import { useFormatters } from '../../contexts/PreferencesContext';
import { Button } from '../../components/ui/button';
import { Checkbox } from '../../components/ui/checkbox';
import { CheckCheck, Clock, Copy, Undo2, Wrench } from 'lucide-react';

/** Meaning of the raw ERP order type codes. */
export const ERP_ORDER_TYPE_LABELS: Record<string, string> = {
  '1': 'Standard / spot PO',
  '4': 'Scheduling agreement',
};

/** Label, badge colour and repair hint per alignment. */
export const ALIGNMENT_META: Record<ErpShortageAlignment, { label: string; badge: string; hint: string }> = {
  aligned: {
    label: 'Aligned', badge: 'bg-green-100 text-green-800',
    hint: 'Helix balance matches the ERP balance',
  },
  accepted: {
    label: 'Accepted difference', badge: 'bg-slate-200 text-slate-700',
    hint: 'A difference accepted as correct while the figures stay the same',
  },
  helix_received_more: {
    label: 'Helix received more', badge: 'bg-red-100 text-red-800',
    hint: 'Helix balance is below the ERP balance — receipts booked in Helix the ERP does not count against this line',
  },
  helix_missing_receipts: {
    label: 'Receipts missing in Helix', badge: 'bg-red-100 text-red-800',
    hint: 'Helix balance is above the ERP balance — the ERP has receipts Helix has not allocated to this line',
  },
  cancelled_in_helix: {
    label: 'Cancelled in Helix', badge: 'bg-red-100 text-red-800',
    hint: 'The ERP still expects this line but Helix has cancelled it',
  },
  short_in_helix_only: {
    label: 'Short in Helix only', badge: 'bg-orange-100 text-orange-800',
    hint: 'The ERP has this line cleared but Helix still shows quantity outstanding',
  },
};

const NOT_DUPLICATE_HINT = 'Not a next-day ERP duplicate. Only rows in a next-day duplicate pair can be selected and fixed in bulk.';
const NO_DUPLICATES_ON_PAGE_HINT = 'No next-day ERP duplicates on this page. Use the Next-day ERP duplicates card to list them.';

interface ErpShortageTableProps {
  rows: ErpShortageRow[];
  canRepair: boolean;
  canAccept: boolean;
  onRepair: (row: ErpShortageRow) => void;
  onAccept: (row: ErpShortageRow) => void;
  onRevoke: (row: ErpShortageRow) => void;
  /** Schedule id whose revoke is in flight. */
  revokingId: number | null;
  /** Show the select column: rows in a next-day ERP duplicate pair can be ticked. */
  selectable: boolean;
  /** Ticked pairs, by their earlier slot's schedule id. */
  selected: Set<number>;
  /** Tick or untick pairs (by earlier slot's schedule id). */
  onSelect: (earlierScheduleIds: number[], checked: boolean) => void;
}

function overdueTone(days: number): string {
  if (days > 30) return 'bg-red-100 text-red-800';
  if (days > 14) return 'bg-orange-100 text-orange-800';
  if (days > 7) return 'bg-amber-100 text-amber-800';
  return 'bg-yellow-50 text-yellow-800';
}

export default function ErpShortageTable({
  rows, canRepair, canAccept, onRepair, onAccept, onRevoke, revokingId, selectable, selected, onSelect,
}: ErpShortageTableProps) {
  const { formatDate, formatDateTime, formatNumber } = useFormatters();
  const showActions = canRepair || canAccept;
  const pageIds = Array.from(new Set(rows.flatMap((r) => (r.next_day_duplicate ? [r.next_day_duplicate.earlier_schedule_id] : []))));
  const tickedOnPage = pageIds.filter((id) => selected.has(id)).length;
  const headerState = tickedOnPage === 0 ? false : tickedOnPage === pageIds.length ? true : 'indeterminate';

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b bg-muted/40 text-left">
            {selectable && (
              <th className="py-2 pl-3 pr-0 w-8">
                {/* Wrapped so the tooltip still shows while the checkbox is disabled. */}
                <span className="inline-flex" title={pageIds.length === 0 ? NO_DUPLICATES_ON_PAGE_HINT : 'Select every next-day duplicate on this page'}>
                  <Checkbox
                    checked={headerState}
                    disabled={pageIds.length === 0}
                    onCheckedChange={(v) => onSelect(pageIds, v === true)}
                    aria-label="Select every next-day duplicate on this page"
                  />
                </span>
              </th>
            )}
            <th className="py-2 px-3 font-medium">PO / Line</th>
            <th className="py-2 px-3 font-medium">ERP Order Type</th>
            <th className="py-2 px-3 font-medium">Part</th>
            <th className="py-2 px-3 font-medium">Supplier</th>
            <th className="py-2 px-3 font-medium">Buyer / Whse</th>
            <th className="py-2 px-3 font-medium">Due Date</th>
            <th className="py-2 px-3 font-medium text-right">Overdue</th>
            <th className="py-2 px-3 font-medium text-right bg-blue-50/60">ERP Balance</th>
            <th className="py-2 px-3 font-medium text-right">Helix Balance</th>
            <th className="py-2 px-3 font-medium text-right">Difference</th>
            <th className="py-2 px-3 font-medium">Helix vs ERP</th>
            <th className="py-2 px-3 font-medium">Last Receipt</th>
            {showActions && <th className="py-2 px-3 font-medium">Action</th>}
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const meta = ALIGNMENT_META[r.alignment];
            const rowTone = r.needs_repair ? 'bg-red-50/60 border-l-4 border-l-red-400' : r.alignment === 'accepted' ? 'bg-slate-50' : '';
            const diff = Number(r.difference);
            const dup = r.next_day_duplicate;
            return (
              <tr key={r.schedule_id} className={`border-b last:border-0 align-top ${rowTone}`} data-alignment={r.alignment}>
                {selectable && (
                  <td className="py-2 pl-3 pr-0">
                    {dup ? (
                      <Checkbox
                        checked={selected.has(dup.earlier_schedule_id)}
                        onCheckedChange={(v) => onSelect([dup.earlier_schedule_id], v === true)}
                        aria-label={`Select next-day duplicate ${r.po_number}/${r.line_number} sched ${r.schedule_line_number ?? ''}`}
                      />
                    ) : (
                      <span className="inline-flex" title={NOT_DUPLICATE_HINT}>
                        <Checkbox checked={false} disabled aria-label={`${r.po_number}/${r.line_number} sched ${r.schedule_line_number ?? ''} can't be selected: ${NOT_DUPLICATE_HINT}`} />
                      </span>
                    )}
                  </td>
                )}
                <td className="py-2 px-3 whitespace-nowrap">
                  <div className="font-medium">{r.po_number}/{r.line_number}</div>
                  <div className="text-xs text-muted-foreground">Sched {r.schedule_line_number ?? '—'}</div>
                </td>
                <td className="py-2 px-3">
                  {r.erp_order_type
                    ? <span className="px-2 py-1 bg-muted rounded text-xs font-medium" title={ERP_ORDER_TYPE_LABELS[r.erp_order_type] || `ERP type ${r.erp_order_type}`}>{r.erp_order_type}</span>
                    : '—'}
                </td>
                <td className="py-2 px-3">
                  <div>{r.part_number ?? '—'}</div>
                  {r.description && (
                    <div className="text-xs text-muted-foreground max-w-[200px] truncate" title={r.description}>{r.description}</div>
                  )}
                </td>
                <td className="py-2 px-3">
                  <div>{r.supplier_name ?? '—'}</div>
                  {r.supplier_code && <div className="text-xs text-muted-foreground">{r.supplier_code}</div>}
                </td>
                <td className="py-2 px-3 whitespace-nowrap">{r.buyer_code ?? '—'} / {r.warehouse_code ?? '—'}</td>
                <td className="py-2 px-3 whitespace-nowrap">{formatDate(r.schedule_date)}</td>
                <td className="py-2 px-3 text-right">
                  <span className={`inline-block rounded px-1.5 py-0.5 text-xs font-medium tabular-nums whitespace-nowrap ${overdueTone(r.days_overdue)}`}>
                    {r.days_overdue} {r.days_overdue === 1 ? 'day' : 'days'}
                  </span>
                </td>
                <td className="py-2 px-3 text-right tabular-nums font-semibold bg-blue-50/60 whitespace-nowrap"
                  title={r.on_erp_report ? 'On the ERP shortage report' : 'The ERP reports nothing outstanding'}>
                  {r.erp_balance_qty != null ? formatNumber(r.erp_balance_qty) : '—'}
                </td>
                <td className="py-2 px-3 text-right tabular-nums whitespace-nowrap">
                  {formatNumber(r.balance_qty)}
                  {r.status === 'cancelled' || r.status === 'shipped'
                    ? <div className="text-[10px] text-muted-foreground capitalize">{r.status}</div>
                    : null}
                </td>
                <td className={`py-2 px-3 text-right tabular-nums font-semibold ${diff === 0 ? 'text-muted-foreground' : 'text-red-700'}`}>
                  {diff > 0 ? '+' : ''}{formatNumber(diff)}
                </td>
                <td className="py-2 px-3">
                  <span className={`inline-block rounded px-1.5 py-0.5 text-xs font-medium whitespace-nowrap ${meta.badge}`} title={meta.hint}>
                    {meta.label}
                  </span>
                  {r.exception && r.exception.active && (
                    <div className="mt-1 max-w-[220px] text-[11px] text-muted-foreground"
                      title={`${r.exception.reason} — ${r.exception.accepted_by ?? 'Unknown user'}, ${formatDateTime(r.exception.accepted_at)}`}>
                      <span className="line-clamp-2">“{r.exception.reason}”</span>
                      <span>{r.exception.accepted_by ?? 'Unknown user'}</span>
                    </div>
                  )}
                  {r.exception && !r.exception.active && (
                    <div className="mt-1 text-[11px] text-amber-700" title={r.exception.reason}>
                      Accepted before — figures have changed since
                    </div>
                  )}
                  {dup && (
                    <div className="mt-1">
                      <span className="inline-flex items-center gap-0.5 rounded bg-violet-100 px-1 py-0.5 text-[10px] font-medium text-violet-800 whitespace-nowrap"
                        title={dup.role === 'earlier'
                          ? `The ERP booked this receipt against the next-day copy, Sched ${dup.partner_schedule_line_number ?? '—'} (${formatDate(dup.partner_date)}). Fixing moves the receipt there and cancels this slot.`
                          : `The ERP booked the receipt for Sched ${dup.partner_schedule_line_number ?? '—'} (${formatDate(dup.partner_date)}) against this next-day copy. Fixing moves that receipt here.`}>
                        <Copy className="h-3 w-3" />
                        {dup.role === 'earlier' ? 'ERP duplicate — earlier' : 'ERP duplicate — next day'}
                      </span>
                    </div>
                  )}
                  {r.awaiting_erp && (
                    <div className="mt-1">
                      <span className="inline-flex items-center gap-0.5 rounded bg-amber-100 px-1 py-0.5 text-[10px] font-medium text-amber-800 whitespace-nowrap"
                        title="A receipt reached Helix after the ERP last reported this line — the ERP balance may not include it yet">
                        <Clock className="h-3 w-3" />Awaiting ERP
                      </span>
                    </div>
                  )}
                </td>
                <td className="py-2 px-3 whitespace-nowrap">{r.last_receipt_date ? formatDate(r.last_receipt_date) : '—'}</td>
                {showActions && (
                  <td className="py-2 px-3">
                    <div className="flex flex-col gap-1">
                      {r.needs_repair && canRepair && (
                        <Button variant="outline" size="sm" className="whitespace-nowrap gap-1" onClick={() => onRepair(r)}>
                          <Wrench className="h-3.5 w-3.5" />Repair
                        </Button>
                      )}
                      {r.needs_repair && canAccept && (
                        <Button variant="ghost" size="sm" className="whitespace-nowrap gap-1" onClick={() => onAccept(r)}>
                          <CheckCheck className="h-3.5 w-3.5" />Accept
                        </Button>
                      )}
                      {r.alignment === 'accepted' && canAccept && (
                        <Button variant="ghost" size="sm" className="whitespace-nowrap gap-1"
                          disabled={revokingId === r.schedule_id} onClick={() => onRevoke(r)}>
                          <Undo2 className="h-3.5 w-3.5" />Revoke
                        </Button>
                      )}
                    </div>
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
